<template>
  <div class="container">
    <Loading v-show="loading" />
    <div class="survey-wrapper">
      <h1 class="title">{{ projectName }}</h1>
      <n-card :bordered="false" class="form-card" content-style="padding: 16px;">
        <n-form
          ref="formRef"
          :model="formModel"
          :rules="rules"
          label-placement="left"
          label-width="auto"
          require-mark-placement="right-hanging"
        >
          <div class="section-header">기본 인적 정보</div>
          <n-form-item label="검사기관">
            <span class="readonly-text">주식회사 코코랩</span>
          </n-form-item>
          <n-form-item label="인증코드" path="authCode">
            <n-input
              v-model:value="formModel.authCode"
              placeholder="인증코드를 입력하세요"
            />
          </n-form-item>
          <n-form-item label="기관명">
            <n-input v-model:value="formModel.org_name" :disabled="isOrg" />
          </n-form-item>

          <template
            v-if="formModel.type == 'putt_u' || formModel.type == 'tec'"
          >
            <n-form-item label="학과 또는 전공">
              <n-input v-model:value="formModel.major" :disabled="isOrg" />
            </n-form-item>
            <n-form-item label="학번" path="num">
              <n-input
                v-model:value="formModel.num"
                placeholder="학번을 입력하세요"
              />
            </n-form-item>
          </template>
          <template v-else>
            <n-form-item label="학년">
              <n-input v-model:value="formModel.grade" :disabled="isOrg" />
            </n-form-item>
            <n-form-item label="반">
              <n-input v-model:value="formModel.class" :disabled="isOrg" />
            </n-form-item>
            <n-form-item label="번호" path="num">
              <n-input
                v-model:value="formModel.num"
                placeholder="번호를 입력하세요"
              />
            </n-form-item>
          </template>

          <n-form-item label="성명" path="name">
            <n-input
              v-model:value="formModel.name"
              placeholder="성명을 입력하세요"
            />
          </n-form-item>
          <n-form-item label="성별" path="gender">
            <n-radio-group v-model:value="formModel.gender" name="gender-group">
              <n-space>
                <n-radio value="남">남</n-radio>
                <n-radio value="여">여</n-radio>
              </n-space>
            </n-radio-group>
          </n-form-item>
          <n-form-item label="생년월일" required>
            <div style="display: flex; gap: 4px; width: 100%;">
              <n-form-item path="dobYear" style="flex: 1.3; margin-bottom: 0;">
                <n-auto-complete
                  v-model:value="formModel.dobYear"
                  :options="yearOptions"
                  :get-show="() => true"
                  placeholder="연도"
                  clearable
                />
              </n-form-item>
              <n-form-item path="dobMonth" style="flex: 1; margin-bottom: 0;">
                <n-auto-complete
                  v-model:value="formModel.dobMonth"
                  :options="monthOptions"
                  :get-show="() => true"
                  placeholder="월"
                  clearable
                />
              </n-form-item>
              <n-form-item path="dobDay" style="flex: 1; margin-bottom: 0;">
                <n-auto-complete
                  v-model:value="formModel.dobDay"
                  :options="dayOptions"
                  :get-show="() => true"
                  placeholder="일"
                  clearable
                />
              </n-form-item>
            </div>
          </n-form-item>
          <n-form-item label="이메일" path="email">
            <n-input
              v-model:value="formModel.email"
              placeholder="이메일을 입력하세요"
            />
          </n-form-item>
        </n-form>

        <div class="agreement-container">
          <div class="section-header">개인정보이용동의서</div>
          <div class="agreement-content">
            개인정보보호법에 관한 법률 제3조(개인정보 보호 원칙),
            제15조(개인정보의 수집‧이용), 제17조(개인정보의 제공)에 따라,
            <br /><br />
            PUTT2.0 및 TOCC 응답 결과의 개인 확인, 결과 분석, 결과리포트 발송에
            필요한 귀하의 개인정보 수집을 위한 동의를 구하고자 합니다.
            <br /><br />
            귀하께서 개인정보(기관명(학교명), 학번(또는 반번), 성명, 성별,
            생년월일, email 주소) 수집, 이용에 동의하십니까?
            <br /><br />
            단, 귀하께서 개인정보이용에 ‘동의하지 않음’에 표기한 경우, 응답
            결과의 분석 및 결과리포트 발송이 불가능함을 알려드립니다.
          </div>
          <div class="agreement-radios">
            <n-radio-group v-model:value="isAgreed">
              <n-space>
                <n-radio value="agree">동의함</n-radio>
                <n-radio value="disagree">동의하지 않음</n-radio>
              </n-space>
            </n-radio-group>
          </div>
        </div>

        <n-button
          :loading="isSubmitting"
          type="primary"
          @click="startSurvey"
          :disabled="isAgreed !== 'agree'"
          block
          size="large"
          style="margin-top: 20px"
        >
          진단시작
        </n-button>
      </n-card>
      <div class="info-text">
        <span>{{ projectName }}</span>
        <span>진단은 외부 서비스(Google Form)를 통해 진행됩니다.</span>
        <span>&nbsp;</span>
        <span><b>COKOLab. Inc.</b></span>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import UserNavbar from "@/components/UserNavbar.vue";
import UserFooter from "@/components/UserFooter.vue";
import Loading from "@/components/Loading.vue";
import { ref, h, onMounted, computed } from "vue";
import * as common from "@/assets/common/common";
import { useRoute } from "vue-router";
import {
  useNotification,
  NForm,
  NFormItem,
  NInput,
  NButton,
  NRadioGroup,
  NRadio,
  NSpace,
  NAutoComplete,
  NCard,
  FormInst,
  FormRules,
} from "naive-ui";
import { getCallable, makeCallApiParam } from "@/config/env";
import { useActionDialog } from "@/composables/useDialog"; // useActionDialog import 추가
const { show: showActionDialog } = useActionDialog();

const getSurveyInfo = "getSurveyInfo";
const checkSurveyInfo = "checkSurveyInfo";
const callApiCallable = getCallable("callApi");

const isAgreed = ref<string | null>(null);
const route = useRoute();
const projectSKFromRoute = ref<string | null>(null);
const projectName = ref<string | null>(null);
const isSubmitting = ref(false);
const loading = ref(false);
const formRef = ref<FormInst | null>(null);
const isOrg = ref(false);
const notification = useNotification();
interface SurveyInfo {
  authCode: string;
  dobYear: string;
  dobMonth: string;
  dobDay: string;
  email: string;
  gender: string;
  name: string;
  major: string;
  grade: string; // Can be string or number, handle as string
  class: string; // Can be string or number, handle as string
  num: string;
  form_id: string;
  org_name: string;
  responder_uri: string;
  type: string;
  entry_ids: string[];
}

const formModel = ref<SurveyInfo>({
  authCode: "",
  dobYear: "",
  dobMonth: "",
  dobDay: "",
  email: "",
  gender: "",
  name: "",
  major: "",
  grade: "",
  class: "",
  num: "",
  form_id: "",
  org_name: "",
  responder_uri: "",
  type: "",
  entry_ids: [],
});

const rules: FormRules = {
  authCode: [
    {
      required: true,
      message: "인증코드를 입력해주세요.",
      trigger: ["input", "blur"],
    },
  ],
  num: [
    {
      required: true,
      message: "학번 또는 번호를 입력해주세요.",
      trigger: ["input", "blur"],
    },
  ],
  name: [
    {
      required: true,
      message: "성명을 입력해주세요.",
      trigger: ["input", "blur"],
    },
  ],
  gender: [
    { required: true, message: "성별을 선택해주세요.", trigger: "change" },
  ],
  dobYear: [
    { required: true, message: "연도 입력", trigger: ["input", "blur", "change"] },
    { pattern: /^[0-9]{4}$/, message: "4자리 숫자", trigger: ["input", "blur"] }
  ],
  dobMonth: [
    { required: true, message: "월 입력", trigger: ["input", "blur", "change"] },
    { pattern: /^(0?[1-9]|1[012])$/, message: "1~12", trigger: ["input", "blur"] }
  ],
  dobDay: [
    { required: true, message: "일 입력", trigger: ["input", "blur", "change"] },
    { pattern: /^(0?[1-9]|[12][0-9]|3[01])$/, message: "1~31", trigger: ["input", "blur"] }
  ],
  email: [
    {
      required: true,
      message: "이메일을 입력해주세요.",
      trigger: ["input", "blur"],
    },
    {
      type: "email",
      message: "올바른 이메일 형식이 아닙니다.",
      trigger: ["blur", "input"],
    },
  ],
};

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 100 }, (_, i) => ({
  label: String(currentYear - i),
  value: String(currentYear - i),
}));
const monthOptions = Array.from({ length: 12 }, (_, i) => ({
  label: String(i + 1).padStart(2, "0"),
  value: String(i + 1).padStart(2, "0"),
}));
const dayOptions = Array.from({ length: 31 }, (_, i) => ({
  label: String(i + 1).padStart(2, "0"),
  value: String(i + 1).padStart(2, "0"),
}));

function moveSurvey() {
  const {
    authCode,
    org_name,
    type,
    major,
    grade,
    class: className,
    num,
    name,
    gender,
    dobYear,
    dobMonth,
    dobDay,
    email,
    entry_ids,
    responder_uri,
  } = formModel.value;

  const valuesToSubmit: string[] = [];

  valuesToSubmit.push(authCode.toUpperCase());
  valuesToSubmit.push(org_name);
  if (type === "putt_u" || type === "tec") {
    valuesToSubmit.push(num);
    valuesToSubmit.push(major);
  } else {
    const classNameStr = isNaN(Number(className)) ? className.toString() : className.toString().padStart(2, "0");
    const numStr = isNaN(Number(num)) ? num.toString() : num.toString().padStart(2, "0");
    valuesToSubmit.push(`${grade}_${classNameStr}_${numStr}`);
  }
  const formattedDob = `${dobYear}${dobMonth.padStart(2, "0")}${dobDay.padStart(2, "0")}`;
  valuesToSubmit.push(name, gender, formattedDob, email);

  if (entry_ids.length > 0 && entry_ids.length === valuesToSubmit.length) {
    const params = new URLSearchParams();
    entry_ids.forEach((id, index) => {
      params.append(id, valuesToSubmit[index]);
    });
    const surveyUrl = `${responder_uri}?usp=pp_url&${params.toString()}`;
    window.location.href = surveyUrl;
  } else {
    // notification.error({
    //     title: "오류",
    //     description: "진단 양식 정보를 구성하는 데 실패했습니다.",
    //     duration: 5000,
    // });
    window.location.href = responder_uri;
  }
  isSubmitting.value = false;
}

async function startSurvey() {
  try {
    await formRef.value?.validate();
  } catch (errors) {
    notification.error({
      title: "입력 오류",
      description: "필수 정보를 모두 입력해주세요.",
      duration: 3000,
    });
    return;
  }
  isSubmitting.value = true;

  const result = await callApiCallable(
    makeCallApiParam(checkSurveyInfo, {
      SK: projectSKFromRoute.value,
      authCode: formModel.value.authCode.toUpperCase(),
    })
  );
  if (common.evalFunctionResult(result)) {
    moveSurvey();
  } else {
    const code = common.getResultCode(result);
    if (code == "110") {
      showActionDialog({
        title: "경고",
        content: () =>
          h("div", null, [
            h(
              "p",
              { style: { color: "red", fontWeight: "bold" } },
              "이미 사용한 인증코드입니다."
            ),
            h("p", "무시하고 다시 검사를 진행 하시겠습니까?"),
          ]),
        positiveText: "확인",
        negativeText: "취소",
        positiveButtonProps: { type: "error" },
        confirmButtonLoadingText: "처리 중...",
        onConfirm: async () => {
          try {
            moveSurvey();
          } catch (error: any) {
            throw error;
          }
        },
      });
    } else {
      const err = common.getResultError(result);
      notification.error({
        title: "오류",
        description: err,
        duration: 5000,
      });
    }
    isSubmitting.value = false;
  }
}

onMounted(async () => {
  loading.value = true;
  projectSKFromRoute.value = route.params.SK as string;
  isOrg.value = projectSKFromRoute.value.startsWith("project#org#");
  projectSKFromRoute.value = decodeURIComponent(projectSKFromRoute.value);
  const result = await callApiCallable(
    makeCallApiParam(getSurveyInfo, { SK: projectSKFromRoute.value })
  );
  if (common.evalFunctionResult(result)) {
    const surveyInfo: SurveyInfo = common.getItemFromFunctionResult(result);
    formModel.value = { ...surveyInfo, dobYear: "", dobMonth: "", dobDay: "" };
    formModel.value.num = "";
    projectName.value = common.getProjectName(formModel.value.type);
    if (isOrg.value == false) {
      formModel.value.org_name = "";
    }

    loading.value = false;
  } else {
    loading.value = false;
    notification.error({
      title: "조회 실패",
      description:
        common.getResultError(result) ||
        "진단 정보를 불러오는 중 오류가 발생했습니다.",
      duration: 3000,
    });
  }
});
</script>
<style scoped>
.container {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 30px 12px;
  background-color: #f4f7f6;
  min-height: calc(100vh - 150px); /* Navbar/Footer 높이 제외 */
}

.survey-wrapper {
  width: 100%;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.title {
  margin-bottom: 24px;
  color: #333;
  font-size: 22px;
}

.form-card {
  width: 100%;
  border-radius: 8px;
}

.readonly-text {
  color: #333; /* 색상을 더 진하게 변경 */
  font-weight: 500;
}

/* Naive UI의 비활성화된 Input 필드 텍스트 색상을 진하게 변경 */
.form-card :deep(.n-input.n-input--disabled .n-input__input-el) {
  color: #333;
  -webkit-text-fill-color: #333; /* Safari/Chrome 등에서 색상 강제 적용 */
}

.info-text {
  margin-top: 16px;
  font-size: 12px;
  color: #888;
  text-align: center;
  display: flex;
  flex-direction: column;
}

.agreement-container {
  margin-top: 24px;
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #fafafa;
}

.agreement-title {
  font-weight: bold;
  margin-bottom: 12px;
  text-align: center;
  color: #333;
}

.agreement-content {
  font-size: 13px;
  color: #555;
  line-height: 1.5;
  margin-bottom: 16px;
}

.section-header {
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: bold;
  color: #18a058;
  margin-bottom: 20px;
}

.section-header::before {
  content: "";
  width: 5px;
  height: 18px;
  background-color: #18a058;
  margin-right: 10px;
}

.agreement-radios {
  display: flex;
  justify-content: center;
}
</style>
